import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";

// Single in-memory cache for /messages so multiple hook instances share data.
let _cache = null;
let _inflight = null;
const _subscribers = new Set();

async function loadAll() {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = api.get("/messages").then((r) => {
    _cache = {};
    (r.data || []).forEach((m) => { _cache[m.key] = m.content || {}; });
    _inflight = null;
    _subscribers.forEach((fn) => fn());
    return _cache;
  }).catch(() => { _inflight = null; return {}; });
  return _inflight;
}

// Force a refresh (e.g. after admin edits)
export function invalidatePlatformMessages() {
  _cache = null;
  loadAll();
}

function interpolate(text, vars) {
  if (!vars || typeof text !== "string") return text;
  return text.replace(/\{(\w+)\}/g, (_, k) => (vars[k] != null ? String(vars[k]) : `{${k}}`));
}

/**
 * usePlatformMessages — fetches all platform messages once and exposes:
 *   m(key, vars?, fallback?) — returns the localized text for the current i18n lang,
 *   falling back to PT, then to the optional `fallback` string, then to the key itself.
 */
export function usePlatformMessages() {
  const { lang } = useI18n();
  const [, setTick] = useState(0);

  useEffect(() => {
    let mounted = true;
    const onChange = () => { if (mounted) setTick((t) => t + 1); };
    _subscribers.add(onChange);
    if (!_cache) loadAll();
    else onChange();
    return () => { mounted = false; _subscribers.delete(onChange); };
  }, []);

  const m = useCallback((key, vars, fallback) => {
    const entry = _cache?.[key];
    if (!entry) return interpolate(fallback || key, vars);
    const text = entry[lang] || entry.pt || entry.en || entry.es || fallback || key;
    return interpolate(text, vars);
  }, [lang]);

  return { m };
}
